import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import { ConfigurationProductDto } from '@/api-sdk';
import {
  ConfigurationContextType,
  global,
  GlobalComponentType,
  GlobalConfigurationContext,
} from '@/context';
import { BREAKPOINT_MEDIUM_PX, BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles } from '@/styles';
import { Variant } from '@/types';
import { ReducedVariants } from './utils';

@customElement('fg-configuration-header')
@localized()
export class FgConfigurationHeader extends LitElement {
  @property({ type: Object })
  product?: ConfigurationProductDto;

  @property({ type: Number })
  printOptionsLength = 0;

  @property({ type: Object })
  reducedVariants?: ReducedVariants;

  @property({ type: String })
  orderStatus?: string | null;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: GlobalConfigurationContext })
  configurationContext!: ConfigurationContextType;

  render() {
    if (!this.product || !this.reducedVariants) {
      return null;
    }

    const { code, image, name } = this.product;
    const { sizes, totalQuantity, version } = this.reducedVariants;
    const { layout } = this.globalContext;
    const { modalInset, variant, configurationData, hideConfigurationSummary } =
      this.configurationContext;

    const { totalPrice } = configurationData;

    return html`
      ${(layout === 'retail' || variant === Variant.SIMPLE) && image
        ? html`
            <div
              class=${classMap({
                'simple-images-container': variant === Variant.SIMPLE,
              })}
            >
              ${Array(variant === Variant.SIMPLE ? 3 : 1)
                .fill(null)
                .map(
                  () => html`
                    <div class="image">
                      <img src=${image} width="100" height="100" alt=${name} />
                    </div>
                  `,
                )}
            </div>
          `
        : nothing}
      <div
        class=${classMap({
          'simple-intro-container': variant === Variant.SIMPLE,
        })}
      >
        ${layout === 'wholesale' && variant !== Variant.SIMPLE && code
          ? html`
              <span class="product_code">${code}</span>
            `
          : nothing}
        ${this.orderStatus
          ? html`
              <span>${this.orderStatus}</span>
            `
          : nothing}
        ${modalInset
          ? html`
              <h5>${name}</h5>
            `
          : html`
              <h2
                class=${classMap({
                  'simple-title': variant === Variant.SIMPLE,
                })}
              >
                ${name}
              </h2>
            `}
        ${version && layout === 'retail'
          ? html`
              <span class="variant">
                ${unsafeHTML(
                  t('content.configuration.version', { version: version }),
                )}
              </span>
            `
          : nothing}
        ${layout === 'retail'
          ? unsafeHTML(
              `<p>
              ${t(
                sizes?.length > 0
                  ? 'content.configuration.intro'
                  : 'content.configuration.introWithoutSizes',
                {
                  quantity: totalQuantity,
                  printOptions: this.printOptionsLength,
                  sizes: sizes.length,
                },
              )}
            </p>`,
            )
          : unsafeHTML(
              `<p class="intro ${variant === Variant.SIMPLE ? 'simple-intro' : ''}">
              ${
                totalQuantity > 0
                  ? `<span>${t('content.configuration.quantity', {
                      count: totalQuantity,
                    })}</span>`
                  : ''
              }
              ${
                this.printOptionsLength > 0
                  ? `<span><span>|</span>${t(
                      'content.configuration.printOptions',
                      { count: this.printOptionsLength },
                    )}</span>`
                  : ''
              }
              ${
                sizes.length > 0
                  ? `<span><span>|</span>${t('content.configuration.sizes', {
                      count: sizes.length,
                    })}</span>`
                  : ''
              }
            </p>`,
            )}
        ${variant === Variant.SIMPLE && !hideConfigurationSummary
          ? html`
              <fg-configuration-date></fg-configuration-date>
            `
          : nothing}
        ${totalPrice && variant === Variant.SIMPLE && !hideConfigurationSummary
          ? html`
              <div class="simple-total-price">
                <div>
                  <span class="total">
                    ${totalPrice.excludingVat.formatted || '-'}
                  </span>
                </div>
                ${totalPrice.includingVat && layout === 'retail'
                  ? html`
                      <div class="simple-total-price-incl">
                        <span>${totalPrice.includingVat.formatted || '-'}</span>
                        <span class="simple-total-price-incl-label">
                          (${t('labels.incVat')})
                        </span>
                      </div>
                    `
                  : nothing}
              </div>
            `
          : nothing}
      </div>
    `;
  }

  static styles = [
    baseStyles,
    css`
      :host {
        display: flex;
        gap: 20px;
        padding-bottom: 10px;
        margin-top: 20px;
      }

      span {
        display: block;
        margin-bottom: 15px;
      }

      h2 {
        font-size: 1rem;
        color: var(--fg-gray--900, var(--fg-fallback-gray--900));
      }

      h5 {
        font-size: 0.875rem;
        color: var(--fg-gray--900, var(--fg-fallback-gray--900));
      }

      p {
        line-height: 1.6;
        margin-top: 4px;
      }

      p.intro {
        display: flex;
        font-size: 0.875rem;
        flex-wrap: wrap;
      }

      p.intro strong {
        margin-right: 4px;
      }

      p.intro > span {
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
        display: flex;
        margin: 0;
      }

      p.intro > span span {
        color: var(--fg-gray--400, var(--fg-fallback-gray--400));
        margin-inline: 3px;
        margin-bottom: 0;
      }
      .product_code {
        color: #000000;
        font-size: 0.875rem;
        margin-bottom: 5px;
      }

      .image {
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        aspect-ratio: 1 / 1;
        position: relative;
        overflow: hidden;
        max-width: 100px;
        width: 50px;
        height: 50px;
        background: white;
      }

      .simple-images-container .image {
        --offset: -3px;
        position: absolute;
      }

      .simple-images-container .image:nth-child(1) {
        top: 0;
        left: 0;
      }

      .simple-images-container .image:nth-child(2) {
        top: var(--offset);
        left: var(--offset);
      }

      .simple-images-container .image:nth-child(3) {
        top: calc(var(--offset) * 2);
        left: calc(var(--offset) * 2);
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }

      .variant {
        font-size: 1rem;
        line-height: 1.6;
      }

      .simple-total-price {
        display: flex;
        flex-direction: row;
        font-size: 1.25rem;
        font-weight: 700;
      }

      .simple-total-price-incl {
        display: flex;
        flex-direction: row;
        font-size: 1rem;
        font-weight: 400;
        align-self: flex-end;
        margin-left: 5px;
      }

      .simple-total-price-incl-label {
        margin-left: 2px;
      }

      fg-configuration-date {
        margin-top: 4px;
        margin-bottom: 4px;
        display: flex;
        font-size: 1rem;
      }

      .simple-images-container {
        position: relative;
        width: 100px;
        height: 100px;
      }

      .simple-intro-container {
        align-self: flex-start;
        font-size: 1rem;
      }

      .simple-title {
        font-size: 1.125rem;
      }

      p.simple-intro > span,
      p.simple-intro > span span {
        color: var(--fg-gray--900, var(--fg-fallback-gray--900));
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        :host {
          font-size: 1rem;
          align-items: center;
          margin: 0;
        }

        h2 {
          font-size: 1.5rem;
        }

        h5 {
          font-size: 1.125rem;
        }

        p.intro > span span {
          margin-inline: 4px;
        }
      }

      @media screen and (max-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .simple-images-container {
          display: none;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
        .image {
          width: 100px;
          height: 100px;
        }
      }
    `,
  ];
}
